// Hiding the body under a garment — the pure half.
//
// A garment and the skin beneath it are two surfaces millimetres apart, both
// skinned by the same joints but by DIFFERENT weights, so they do not deform
// identically: wherever the skin's answer ends up outside the cloth's, the body
// pokes through. Observed on isaac-body-marvelous, at the knees, shins and
// thighs.
//
// It is not z-fighting and no depth trick helps — the body really is outside
// the garment there. Nor is it a binding fault: it is what two independently
// skinned surfaces in contact always do. Unreal solves it the same way this
// module does, by simply not drawing the covered skin, which is why a MetaHuman
// published WITH clothes arrives with its under-clothing body already culled
// (the reason aoi-fridaytest never showed this and a body-only export does).
//
// Two rules keep the cull from being worse than the poke:
//
//   1. ONLY SKIN THAT IS INSIDE. A body vertex in FRONT of the cloth is skin
//      the viewer is meant to see — an arm beside a sleeve, a neck above a
//      collar. Hiding it punches a hole in the character, which is a far worse
//      artefact than the one being fixed.
//   2. ONLY SKIN THAT IS CLOSE. Poking happens where the garment is TIGHT; skin
//      deep inside a baggy jogger cannot reach the outside and does not need
//      hiding. A generous radius would also swallow the skin just past a hem or
//      a sleeve's end, opening a gap at the ankle or wrist.
//
// Both rules err the same way — toward leaving skin visible — because a missed
// poke is a small blemish and a hole is not.
//
// No three.js import here, so node:test drives the maths.

/**
 * Metres. Beyond this, cloth is not resting on the skin and the skin cannot
 * come through it. Deliberately tight; see rule 2 above.
 */
export const CULL_MAX_DIST = 0.03

/**
 * How far OUTSIDE the cloth a body vertex may still count as covered.
 *
 * This one is counter-intuitive and was wrong at first. The strict reading is
 * zero — hide only skin that is genuinely inside the garment — but a vertex
 * that POKES THROUGH is by definition outside it, so a strict test culls all
 * the skin that was never visible and leaves every pixel of the problem.
 * Measured on isaac-body-marvelous, with a 2 mm tolerance the torso and thighs
 * came clean and the shins still showed.
 *
 * 12 mm is picked from the separation in that measurement, not by taste. Of the
 * body vertices sitting outside the cloth:
 *
 *   - the shin pokes are 0.5 cm out at the median, 0.9 cm at p90, with cloth
 *     0.6 cm away — barely outside, and hugged by fabric;
 *   - the bare feet below the cuff are 13.2 cm out with cloth 16.9 cm away;
 *   - the bare arms are 1.5 cm out at the median with cloth 12.7 cm away.
 *
 * So `CULL_MAX_DIST` excludes the feet and arms by proximity, and this
 * tolerance admits the pokes. What it also admits is the thin ring of skin
 * right at a sleeve or cuff opening, which is within a centimetre of the hem on
 * both counts — acceptable, because that skin sits immediately behind the
 * opening and the garment itself covers where it used to be.
 */
export const CULL_OUTSIDE_TOLERANCE = 0.012

/**
 * Which body vertices are underneath the garment.
 *
 * @param {object} args
 * @param {ArrayLike<number>} args.bodyPositions  xyz triples, world space
 * @param {object} args.grid            VertexGrid over the garment's world-space vertices
 * @param {ArrayLike<number>} args.garmentNormals xyz triples, parallel to the grid's positions, world space
 * @param {number} [args.maxDist]
 * @param {number} [args.outsideTolerance]
 * @returns {Uint8Array} 1 per covered vertex
 */
export function coveredBodyVertices({
  bodyPositions, grid, garmentNormals,
  maxDist = CULL_MAX_DIST, outsideTolerance = CULL_OUTSIDE_TOLERANCE,
}) {
  const n = Math.floor(bodyPositions.length / 3)
  const covered = new Uint8Array(n)
  if (!grid || grid.count === 0 || !garmentNormals) return covered
  for (let i = 0; i < n; i++) {
    const x = bodyPositions[i * 3], y = bodyPositions[i * 3 + 1], z = bodyPositions[i * 3 + 2]
    const v = grid.nearest(x, y, z)
    if (v < 0 || grid.lastDist > maxDist) continue
    // Signed side: positive is outside the cloth, along its own normal.
    const side =
      (x - grid.positions[v * 3]) * garmentNormals[v * 3] +
      (y - grid.positions[v * 3 + 1]) * garmentNormals[v * 3 + 1] +
      (z - grid.positions[v * 3 + 2]) * garmentNormals[v * 3 + 2]
    if (side <= outsideTolerance) covered[i] = 1
  }
  return covered
}

/**
 * A triangle index with every fully-covered triangle removed.
 *
 * A triangle is dropped only when ALL THREE of its corners are covered. A
 * partly covered triangle straddles the garment's edge and is exactly the skin
 * that must keep being drawn, or the boundary frays.
 *
 * Returns `index: null` to mean UNCHANGED — distinct from an empty index, which
 * means the mesh is entirely under cloth and should vanish. A caller that
 * conflated the two would either rebuild every buffer for nothing or delete a
 * mesh it meant to keep.
 *
 * @param {ArrayLike<number>|null} index  null for a non-indexed mesh
 * @param {Uint8Array} covered
 * @returns {{ index: ArrayLike<number>|null, removed: number, kept: number }}
 */
export function cullTriangles(index, covered) {
  const vertexCount = covered.length
  const tris = index ? Math.floor(index.length / 3) : Math.floor(vertexCount / 3)
  const at = (i) => (index ? index[i] : i)
  let removed = 0
  for (let t = 0; t < tris; t++) {
    if (covered[at(t * 3)] && covered[at(t * 3 + 1)] && covered[at(t * 3 + 2)]) removed++
  }
  if (removed === 0) return { index: null, removed: 0, kept: tris }

  const kept = tris - removed
  // Keep the caller's index width: a mesh over 65k vertices truncates silently
  // in a Uint16Array, which renders as a shredded surface rather than an error.
  const Ctor = index
    ? index.constructor
    : (vertexCount > 65535 ? Uint32Array : Uint16Array)
  const out = new Ctor(kept * 3)
  let w = 0
  for (let t = 0; t < tris; t++) {
    const a = at(t * 3), b = at(t * 3 + 1), c = at(t * 3 + 2)
    if (covered[a] && covered[b] && covered[c]) continue
    out[w++] = a; out[w++] = b; out[w++] = c
  }
  return { index: out, removed, kept }
}
